import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Lightbulb, Search, Loader2, Copy, Check, HelpCircle, GitCompareArrows, Hash, Download } from 'lucide-react'
import api from '../lib/api'
import { toast } from '../lib/toast'
import Dropdown from '../components/Dropdown'
import { useSeoHead } from '../lib/seo'

const LANGS = [
  { value: 'cs', label: 'Čeština (CZ)' }, { value: 'sk', label: 'Slovenčina (SK)' },
  { value: 'en', label: 'English (US)' }, { value: 'de', label: 'Deutsch (DE)' },
  { value: 'pl', label: 'Polski (PL)' }, { value: 'it', label: 'Italiano (IT)' },
  { value: 'hu', label: 'Magyar (HU)' }, { value: 'uk', label: 'Українська (UA)' },
]

export default function KeywordIdeasPage() {
  const { t, i18n } = useTranslation()
  const [seed, setSeed] = useState('')
  const [lang, setLang] = useState((i18n.language || 'cs').slice(0, 2))
  const [copied, setCopied] = useState<string | null>(null)

  useSeoHead({
    title: t('kwideas.meta_title', { defaultValue: 'Návrh klíčových slov zdarma: generátor nápadů | SEO Tools' }),
    description: t('kwideas.meta_desc', { defaultValue: 'Najděte desítky nápadů na klíčová slova zdarma. Reálné dotazy z Google našeptávače, otázky, srovnání i související fráze. Bez registrace.' }),
    canonicalPath: '/navrh-klicovych-slov',
  })

  const run = useMutation({
    mutationFn: () => api.get('/tools/keyword-ideas', { params: { seed: seed.trim(), lang } }).then((r) => r.data),
  })

  const data = run.data
  const groups: { key: string; label: string; icon: any; items: string[] }[] = [
    { key: 'questions', label: t('kwideas.questions', { defaultValue: 'Otázky' }), icon: HelpCircle, items: data?.groups?.questions || [] },
    { key: 'comparisons', label: t('kwideas.comparisons', { defaultValue: 'Srovnání' }), icon: GitCompareArrows, items: data?.groups?.comparisons || [] },
    { key: 'related', label: t('kwideas.related', { defaultValue: 'Související' }), icon: Hash, items: data?.groups?.related || [] },
  ]
  const allItems = groups.flatMap((g) => g.items)

  const downloadCsv = () => {
    const rows = [['skupina', 'klicove_slovo']]
    groups.forEach((g) => g.items.forEach((kw) => rows.push([g.label, kw])))
    const csv = '﻿' + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `klicova-slova-${(data?.seed || 'seed').replace(/[^a-z0-9]+/gi, '-')}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      toast.success(t('share.copied', { defaultValue: 'Zkopírováno' }))
      setTimeout(() => setCopied(null), 1500)
    } catch { /* ignore */ }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-start gap-4 mb-6">
        <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Lightbulb className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{t('kwideas.title', { defaultValue: 'Návrh klíčových slov' })}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl">
            {t('kwideas.subtitle', { defaultValue: 'Zadejte téma a získejte desítky reálných nápadů z Google našeptávače, zdarma a bez registrace.' })}
          </p>
        </div>
      </div>

      {/* Vstup */}
      <div className="bg-white dark:bg-white/5 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-6 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); if (seed.trim().length >= 2) run.mutate() }}
          className="flex flex-col sm:flex-row gap-3">
          <input type="text" value={seed} onChange={(e) => setSeed(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- primary search field of the page, focusing it speeds up the main task
            autoFocus
            placeholder={t('kwideas.placeholder', { defaultValue: 'např. běžecké boty' })}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" />
          <div className="w-full sm:w-48"><Dropdown value={lang} onChange={setLang} options={LANGS} /></div>
          <button type="submit" disabled={seed.trim().length < 2 || run.isPending}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-50 cursor-pointer">
            {run.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {t('kwideas.run', { defaultValue: 'Najít nápady' })}
          </button>
        </form>
      </div>

      {/* Výsledky */}
      {run.isError && <p className="text-red-500 text-sm">{t('common.error', { defaultValue: 'Chyba' })}</p>}
      {data && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {t('kwideas.found', { count: data.total, defaultValue: '{{count}} nápadů pro „{{seed}}"', seed: data.seed })}
            </p>
            {allItems.length > 0 && (
              <div className="flex items-center gap-3">
                <button onClick={() => copy(allItems.join('\n'), '__all__')}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                  {copied === '__all__' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {t('kwideas.copy_all', { defaultValue: 'Kopírovat vše' })}
                </button>
                <button onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-600 cursor-pointer">
                  <Download className="w-4 h-4" />
                  {t('kwideas.export_csv', { defaultValue: 'Stáhnout CSV' })}
                </button>
              </div>
            )}
          </div>

          {allItems.length === 0 ? (
            <p className="text-center text-gray-400 py-12">{t('kwideas.empty', { defaultValue: 'Žádné nápady, zkuste jiné téma.' })}</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {groups.map((g) => g.items.length > 0 && (
                <div key={g.key} className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10 flex items-center justify-between">
                    <span className="font-semibold text-gray-900 dark:text-white text-sm flex items-center gap-2"><g.icon className="w-4 h-4 text-primary-500" />{g.label}</span>
                    <span className="text-xs text-gray-400">{g.items.length}</span>
                  </div>
                  <ul className="divide-y divide-gray-50 dark:divide-white/5 max-h-[420px] overflow-auto">
                    {g.items.map((kw, i) => (
                      <li key={i} className="group flex items-center justify-between gap-2 px-4 py-2 hover:bg-gray-50 dark:hover:bg-white/5">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{kw}</span>
                        <button onClick={() => copy(kw, `${g.key}-${i}`)} title={t('kwideas.copy', { defaultValue: 'Kopírovat' })}
                          className="text-gray-300 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition cursor-pointer shrink-0">
                          {copied === `${g.key}-${i}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
